import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  Stack,
} from '@mui/material'

import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import PasswordField from '../../components/PasswordField'

const Login: React.FC = () => {
  const navigate = useNavigate()
  const { login } = useAuth()
  const { showNotification } = useNotification()

  const DEMO_CREDENTIALS = {
    email: 'demo@lyftr.ai',
    password: 'demo123'
  }

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  })
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [isDemoLoading, setIsDemoLoading] = useState(false)
  const [error, setError] = useState('')

  // Auto demo-login when redirected from Shopify public app install
  const location = useLocation()
  const hasAttemptedAutoLogin = useRef(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rememberMe' ? checked : value,
    }))
  }

  const validateEmail = (email: string) => {
    return /\S+@\S+\.\S+/.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.email || !validateEmail(formData.email)) {
      setError('Please enter a valid email address')
      return
    }
    if (!formData.password) {
      setError('Please enter your password')
      return
    }

    setIsSigningIn(true)
    try {
      await login(formData.email, formData.password)
      showNotification('Login successful!', 'success')
      navigate('/dashboard' + location.search)
    } catch (err) {
      console.error('Login error:', err)
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsSigningIn(false)
    }
  }

  const handleDemoLogin = async () => {
    setError('')
    setIsDemoLoading(true)
    try {
      await login(DEMO_CREDENTIALS.email, DEMO_CREDENTIALS.password)
      showNotification('Welcome to the Demo!', 'success')
      navigate('/dashboard' + location.search)
    } catch (err) {
      console.error('Demo login error:', err)
      setError(err instanceof Error ? err.message : 'Demo login failed')
    } finally {
      setIsDemoLoading(false)
    }
  }

  // Trigger auto demo-login if status=public_install_success is in the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('status') === 'public_install_success' && !hasAttemptedAutoLogin.current) {
      hasAttemptedAutoLogin.current = true
      const session = params.get('session')
      const shopifyShopDomain = params.get('shopify_shop_domain')
      if (shopifyShopDomain) { localStorage.setItem('shopify_shop_domain', shopifyShopDomain) }
      if (session) {
        setIsDemoLoading(true)
        const backend_base_url = (import.meta as any).env.VITE_BACKEND_URL
        fetch(`${backend_base_url}/api/v1/integrations/shopify/verify-session`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session}` }
        })
        .then(() => {
          console.log("Session verified", session)
          setIsDemoLoading(false)
          handleDemoLogin()
        })
        .catch(err => {
          console.error('Session verify error:', err)
          setIsDemoLoading(false)
          handleDemoLogin()
        })
      } else {
        handleDemoLogin()
      }
    }
  }, [location.search])

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        height: { lg: '100vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: { xs: '#ffffff', sm: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)' },
        position: 'relative',
        overflow: { xs: 'auto', lg: 'hidden' },
        px: { xs: 0, sm: 2, md: 4 },
        py: { xs: 2, lg: 2 },
      }}
    >
      {/* Logo — only on desktop (lg+), floats top-right */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'absolute',
          top: 32,
          right: 48,
          zIndex: 1100,
        }}
      >
        <Box
          component="img"
          src="/lyftr-logo.jpg"
          alt="Lyftr AI"
          sx={{
            height: 96,
            width: 96,
            borderRadius: '50%',
            objectFit: 'contain',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            bgcolor: '#fff',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'scale(1.05)' }
          }}
          onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      </Box>

      {/* Card — full-width on mobile, constrained on desktop */}
      <Card
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          maxWidth: { xs: '100%', sm: 480, lg: 1100 },
          minHeight: { lg: 760 },
          maxHeight: { xs: 'none', lg: 'calc(100vh - 48px)' },
          borderRadius: { xs: 0, sm: 6, lg: 8 },
          background: { xs: '#ffffff', lg: 'rgba(255, 255, 255, 0.7)' },
          backdropFilter: { lg: 'blur(16px)' },
          border: { xs: 'none', sm: '1px solid rgba(255, 255, 255, 0.5)' },
          boxShadow: { xs: 'none', sm: '0 40px 80px rgba(0,0,0,0.06)' },
          overflow: 'hidden',
          mx: { xs: 0, sm: 'auto' },
          my: { xs: 0, sm: 4, lg: 0 },
        }}
      >
        {/* Left Side: Illustration — desktop only */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
            width: '55%',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%)',
          }}
        >
          <Box
            component="img"
            src="/auth-side-image.png"
            alt="Collaboration Illustration"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              mixBlendMode: 'multiply',
              opacity: 0.95,
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </Box>

        {/* Right Side: Form */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'flex-start', lg: 'center' },
            overflowY: { xs: 'visible', lg: 'auto' },
          }}
        >
          <CardContent
            sx={{
              p: { xs: 3, sm: 4, lg: 4 },
              pt: { xs: 4, sm: 5, lg: 4 },
              width: '100%',
            }}
          >
            {/* Mobile logo — inline at top of form */}
            <Box
              sx={{
                display: { xs: 'flex', lg: 'none' },
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Box
                component="img"
                src="/lyftr-logo.jpg"
                alt="Lyftr AI"
                sx={{
                  height: { xs: 90, sm: 100 },
                  width: { xs: 90, sm: 100 },
                  borderRadius: '50%',
                  objectFit: 'contain',
                }}
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Typography
                variant="h5"
                fontWeight="900"
                align="center"
                sx={{
                  mb: { xs: 3, lg: 4 },
                  fontSize: { xs: '1.35rem', sm: '1.45rem', lg: '1.55rem' },
                  color: 'text.primary',
                  letterSpacing: -0.5,
                }}
              >
                Login to dashboard
              </Typography>

              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                type="email"
                variant="outlined"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                sx={{ mb: { xs: 2, lg: 3 } }}
                InputProps={{ sx: { borderRadius: 3, fontSize: '1rem' } }}
                inputProps={{ inputMode: 'email', enterKeyHint: 'next' }}
              />

              <PasswordField
                fullWidth
                id="password"
                name="password"
                label="Password"
                variant="outlined"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                sx={{ mb: { xs: 2, lg: 3 } }}
                InputProps={{ sx: { borderRadius: 3, fontSize: '1rem' } }}
                inputProps={{ enterKeyHint: 'done' }}
              />

              <Box sx={{ mb: { xs: 3, lg: 4 } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2">Remember me</Typography>}
                />
              </Box>

              <Stack spacing={2}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSigningIn || isDemoLoading}
                  sx={{
                    py: { xs: 1.75, lg: 2 },
                    fontSize: '1rem',
                    fontWeight: '800',
                    borderRadius: 3.5,
                    minHeight: 48,
                    background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(59, 130, 246, 0.4)',
                    }
                  }}
                >
                  {isSigningIn ? 'Signing in...' : 'Sign In'}
                </Button>

                <Divider>
                  <Typography variant="body2" color="text.secondary" fontWeight="700" sx={{ opacity: 0.6 }}>
                    OR
                  </Typography>
                </Divider>

                <Button
                  fullWidth
                  type="button"
                  variant="outlined"
                  size="large"
                  color="primary"
                  disabled={isSigningIn || isDemoLoading}
                  onClick={handleDemoLogin}
                  sx={{
                    borderRadius: 3.5,
                    py: { xs: 1.75, lg: 2 },
                    minHeight: 48,
                    fontWeight: '800',
                    borderWidth: 2,
                    textTransform: 'none',
                    letterSpacing: 0,
                    transition: 'all 0.3s ease',
                    '&:hover': { borderWidth: 2, transform: 'translateY(-2px)', background: 'rgba(59, 130, 246, 0.05)' },
                  }}
                >
                  {isDemoLoading ? 'Launching Demo...' : 'Try Live Demo'}
                </Button>

                <Box sx={{ mt: { xs: 2, lg: 3 }, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Don't have an account?{' '}
                    <Button
                      variant="text"
                      onClick={() => navigate('/signup')}
                      sx={{
                        p: 0,
                        minWidth: 'auto',
                        fontWeight: 700,
                        textTransform: 'none',
                        letterSpacing: 0,
                        '&:hover': { background: 'none', textDecoration: 'underline' }
                      }}
                    >
                      Sign up
                    </Button>
                  </Typography>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </Box>
      </Card>
    </Box>
  )
}

export default Login
